import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useCallback, useRef } from "react";

type Props = {
	// Comma-separated extensions (e.g. ".csv,.xls,.xlsx"). Also used to filter files dropped via drag-and-drop.
	accept: string;
	// Text shown inside the drop zone.
	hint: string;
	multiple?: boolean;
	onFiles: (files: File[]) => void;
	/**
	 * Files whose extension is not in `accept`. Without this the zone drops them silently and the
	 * picker's own filter does not apply to a drag-and-drop, so the user is left believing a file was
	 * accepted. Optional — a caller that does not care keeps the old silent behaviour.
	 */
	onRejected?: (files: File[]) => void;
	dataTestId?: string;
};

/**
 * Whether `file`'s name ends in one of `accept`'s comma-separated extensions (empty `accept` = anything).
 * Exported so every "accept these file types" control (this drop zone, an inline attach button) filters
 * a picked or dropped file by exactly the same rule.
 */
export const isFileAccepted = (file: File, accept: string): boolean => {
	const exts = accept
		.split(",")
		.map((e) => e.trim().toLowerCase())
		.filter(Boolean);
	if (exts.length === 0) return true;
	const name = file.name.toLowerCase();
	return exts.some((ext) => name.endsWith(ext));
};

export function FileDropZone({ accept, hint, multiple = false, onFiles, onRejected, dataTestId }: Props) {
	const inputRef = useRef<HTMLInputElement>(null);

	const handleFiles = useCallback(
		(fileList: FileList | null) => {
			if (!fileList) return;
			const all = Array.from(fileList);
			const filtered = all.filter((f) => isFileAccepted(f, accept));
			const rejected = all.filter((f) => !isFileAccepted(f, accept));
			if (filtered.length > 0) onFiles(filtered);
			if (rejected.length > 0) onRejected?.(rejected);
		},
		[accept, onFiles, onRejected],
	);

	return (
		<Box
			data-testid={dataTestId}
			sx={{
				border: (theme) => `2px dashed ${theme.palette.primary.main}`,
				borderRadius: 2,
				p: 3,
				textAlign: "center",
				cursor: "pointer",
				mb: 2,
				"&:hover": { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08) },
			}}
			onClick={() => inputRef.current?.click()}
			onDragOver={(e) => {
				e.preventDefault();
				e.stopPropagation();
			}}
			onDrop={(e) => {
				e.preventDefault();
				e.stopPropagation();
				handleFiles(e.dataTransfer.files);
			}}
		>
			<Typography variant="subtitle1">{hint}</Typography>
			<input
				ref={inputRef}
				hidden
				type="file"
				multiple={multiple}
				accept={accept}
				onChange={(e) => {
					handleFiles(e.target.files);
					if (inputRef.current) inputRef.current.value = "";
				}}
			/>
		</Box>
	);
}
